import Tooltip from './components/Tooltip.vue';
import VideoConsent from './components/VideoConsent.vue';

import { readQuestionnaire, createResponse, readResponse, updateResponse, finishResponse } from './../services/apiService.js';

import constants from './../constants.js';

import 'sweetalert2/src/sweetalert2.scss';

const questionnaireEmbedApp = {
    components: {
        Tooltip,
        VideoConsent,
    },
    data() {
        return {
            constants: constants,
            loading: false,
            questionnaire: null,
            showModal: false,
            page: 0,
            answers: {},
            response: null,
            isProgressing: true,
            fullResponse: null,

            dataGuid: '',
            dataCulture: 'nl-NL',
            dataReorder: false,
            dataQuestionXofYLabel: 'Vraag {current} van {total}',
            dataSelectedXofYLabel: '{selected} van {total} geselecteerd',
            dataStopQuestionnaireTitle: 'Weet je zeker dat je wilt stoppen?',
            dataStopQuestionnaireText: 'Alle ingevulde antwoorden zullen hierbij verloren gaan.',
            dataStopQuestionnaireConfirm: 'Ja, ik wil stoppen',
            dataStopQuestionnaireCancel: 'Hervat de test',
        };
    },
    computed: {
        modalTitle() {
            switch (this.page) {
                case 0:
                    return this.questionnaire?.title;
                case this.questionnaire?.questions?.length + 1:
                    return this.questionnaire?.thankyouText;
                default:
                    return `${this.page}. ${this.questionnaire?.questions?.find(q => q.position === this.page)?.text}`;
            }
        },
        activeQuestion() {
            return this.questionnaire?.questions?.find(q => q.position === this.page);
        },
        questionXOfY() {
            return this.dataQuestionXofYLabel.replace(constants.replaces.current, this.page).replace(constants.replaces.total, this.questionnaire?.questions?.length);
        },
        selectedXOfY() {
            if (!this.activeQuestion || this.activeQuestion.maxSelectAmount === 1) return '';
            const answer = this.answers[this.activeQuestion.id];
            if (!Array.isArray(answer)) return '';
            return this.dataSelectedXofYLabel.replace(constants.replaces.selected, answer.length).replace(constants.replaces.total, this.activeQuestion.maxSelectAmount);
        },
        buttonDisabled() {
            if (!this.activeQuestion) return false;
            const answer = this.answers[this.activeQuestion.id];
            if (!answer) return true;
            if (Array.isArray(answer)) {
                return answer.length < this.activeQuestion.minSelectAmount || answer.length > this.activeQuestion.maxSelectAmount;
            }
            return false;
        },
        isAtFinish() {
            return this.page === this.questionnaire?.questions?.length;
        },
    },
    methods: {
        async fetchData() {
            try {
                this.loading = true;
                this.questionnaire = await readQuestionnaire({ id: this.dataGuid, cultureCode: this.dataCulture });
                this.questionnaire?.questions.forEach(q => {
                    if (!(q.minSelectAmount === 1 && q.maxSelectAmount === 1) && q.type !== constants.api.question.types.horizontalScale) {
                        this.answers[q.id] = [];
                    } else {
                        this.answers[q.id] = null;
                    }
                });
            } catch (error) {
                console.error('Error fetching questionnaire data:', error);
            } finally {
                this.loading = false;
            }
        },
        async openModal() {
            if (!this.response) this.response = await createResponse({ questionnaireId: this.questionnaire.id, cultureCode: this.dataCulture });
            this.showModal = true;
            const html = document.documentElement;
            html.style.overflow = 'hidden';
        },
        async closeModalPrompt() {
            const swal = await this.$swal.fire({
                title: this.dataStopQuestionnaireTitle,
                icon: constants.icons.question,
                text: this.dataStopQuestionnaireTèxt,
                confirmButtonText: this.dataStopQuestionnaireConfirm,
                cancelButtonText: this.dataStopQuestionnaireCancel,
            });
            if (swal.isConfirmed) this.closeModal();
        },
        closeModal() {
            this.page = 0;
            this.showModal = false;
            const html = document.documentElement;
            html.style.overflow = 'auto';
            this.response = null;
        },
        async applyFullResponse(fullResponse) {
            this.fullResponse = fullResponse;

            const $widgetGridsQuestionAnswer = document.querySelectorAll('.widget-grid--question-answer');
            $widgetGridsQuestionAnswer.forEach($grid => this.initializeGridQuestionAnswer($grid));

            const $widgetGridsScale = Array.from(document.querySelectorAll('.widget-grid--scale'));
            $widgetGridsScale.forEach($grid => this.initializeGridScale($grid));

            const $container = document.getElementById('questionnaire-results');
            $container.classList.remove('hide');

            if (this.reorder && this.questionnaire.scales && this.questionnaire.scales.length > 1) {
                $widgetGridsScale.sort(($gridA, $gridB) => {
                    const showDataA = JSON.parse($gridA.dataset.showJson);
                    const showDataB = JSON.parse($gridB.dataset.showJson);
                    const scoreA = this.fullResponse.scaleScores[showDataA.id] || 0;
                    const scoreB = this.fullResponse.scaleScores[showDataB.id] || 0;
                    return scoreB - scoreA;
                });

                Array.from($widgetGridsScale).reverse().forEach($grid => $container.prepend($grid));
            }

            const $allGrids = document.querySelectorAll('.widget-grid--question-answer, .widget-grid--scale');

            $allGrids.forEach($grid => {
                if ($grid.classList.contains('hide')) {
                    $grid.remove();
                }
            });

            this.closeModal();

            await this.$nextTick();
            window.dispatchEvent(new CustomEvent('questionnaire:results'));
        },
        prevPage() {
            this.page--;
        },
        nextPage() {
            this.page++;
        },
        async finish() {
            await finishResponse({ questionnaireId: this.questionnaire.id, responseId: this.response.id });
            const fullResponse = await readResponse({ questionnaireId: this.questionnaire.id, responseId: this.response.id });
            await this.applyFullResponse(fullResponse);
        },
        async onInput() {
            let optionValues = this.answers[this.activeQuestion.id];
            if (!Array.isArray(optionValues)) optionValues = [optionValues];

            await updateResponse({
                questionnaireId: this.questionnaire.id,
                responseId: this.response.id,
                questionId: this.activeQuestion.id,
                optionValues: optionValues.map(v => { return { AnswerOptionId: v } }),
            });
        },
        isVideo(type) {
            return type === constants.api.question.mediaTypes.youtube || type === constants.api.question.mediaTypes.vimeo;
        },
        getKey(block) {
            if (!block) return null;
            const id = block.questionId || block.questionAnswer?.questionId;
            return `${id}-${block.title}`;
        },
        getMediaValue(block) {
            if (!block) return null;
            return block.youtubeId || block.vimeoId || block.images?.length || null;
        },
        getMediaType(block) {
            if (!block) return null;
            if (block.youtubeId) return constants.api.question.mediaTypes.youtube;
            if (block.vimeoId) return constants.api.question.mediaTypes.vimeo;
            if (block.images?.length) return constants.api.question.mediaTypes.image;
            return null;
        },
        initializeGridQuestionAnswer($grid) {
            if (!this.fullResponse || !$grid.dataset.showJson) return;
            const showData = JSON.parse($grid.dataset.showJson);
            const answerArray = (this.fullResponse.answers ?? []).find(a => a.questionnaireQuestionId === showData.questionId)?.answerOptionSelections ?? [];
            const selectedArray = answerArray.map(a => a.questionnaireQuestionAnswerOptionId);
            const shouldShow = selectedArray.some(a => showData.selectedAnswers.includes(a));
            if (shouldShow) $grid.classList.remove('hide');
        },
        initializeGridScale($grid) {
            if (!this.fullResponse || !$grid.dataset.showJson) return;
            const showData = JSON.parse($grid.dataset.showJson);
            const scalescore = this.fullResponse.scaleScores[showData.id];
            const shouldShow = scalescore >= showData.min && scalescore <= showData.max;
            if (shouldShow) $grid.classList.remove('hide');
            const $scoreElements = $grid.querySelectorAll('.has-questionnaire-score');
            $scoreElements.forEach($el => {
                $el.innerHTML = $el.innerHTML.replace(/{score}/g, scalescore);
            });
        },
    },
    async mounted() {
        this.dataGuid = this.$el.dataset.guid;
        if (this.$el.dataset.culture) this.dataCulture = this.$el.dataset.culture;
        if (this.$el.dataset.reorder) this.reorder = this.$el.dataset.reorder === 'True' ? true : false;
        if (this.$el.dataset.questionXofyLabel) this.dataQuestionXofYLabel = this.$el.dataset.questionXofyLabel;
        if (this.$el.dataset.selectedXofyLabel) this.dataSelectedXofYLabel = this.$el.dataset.selectedXofyLabel;
        if (this.$el.dataset.stopQuestionnaireTitle) this.dataStopQuestionnaireTitle = this.$el.dataset.stopQuestionnaireTitle;
        if (this.$el.dataset.stopQuestionnaireText) this.dataStopQuestionnaireText = this.$el.dataset.stopQuestionnaireText;
        if (this.$el.dataset.stopQuestionnaireConfirm) this.dataStopQuestionnaireConfirm = this.$el.dataset.stopQuestionnaireConfirm;
        if (this.$el.dataset.stopQuestionnaireCancel) this.dataStopQuestionnaireCancel = this.$el.dataset.stopQuestionnaireCancel;

        await this.fetchData();

        const $container = document.getElementById('questionnaire-results');
        $container.classList.add('hide');

        await this.openModal();
    },
};

export default questionnaireEmbedApp;
